import { Injectable } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { Data } from "@angular/router";
import { AuthStateService } from "@core/auth-state.service";
import { BehaviorSubject, distinctUntilChanged, map, shareReplay, take } from "rxjs";
import { CommonService } from "src/app/services/common/common.service";
import { StoreFirestoreHelperService } from "./services/store-firestore-helper.service";

@Injectable({
  providedIn: "root",
})
export class StoreJackpotRepositoryService {
  userJackpotTrigger$ = new BehaviorSubject<Data>(null);
  jackpots$ = this.storeFirestoreHelper.getAngularFireStore("/jackpot", "real_name").pipe(shareReplay(1));
  sortedJackpots$ = this.storeFirestoreHelper.getAngularFireStore("/jackpot", "expiry_date").pipe(shareReplay(1));
  /** Suppresses stale active status until Firestore confirms inactive. */
  private pendingInactiveTriggerIds = new Set<string>();
  /** Owner of the pending set — cleared/replaced on logout or user switch. */
  private pendingInactiveOwnerUserId: string | null = null;

  constructor(
    private commonService: CommonService,
    private authStateService: AuthStateService,
    private storeFirestoreHelper: StoreFirestoreHelperService,
    private angularFireAuth: AngularFireAuth,
  ) {
    this.angularFireAuth.authState
      .pipe(
        map((user) => user?.uid ?? null),
        distinctUntilChanged()
      )
      .subscribe((userId) => {
        this.bindPendingInactiveToUser(userId);
      });
  }

  // This Method is used to get User Jackpot Trigger
  getUserJackpotTrigger() {
    // ✅ FIX: Gate on customTokenReady$ before opening the Firestore listener
    // to prevent permission-denied race condition (LOTTOSOCIAL-PWA-1MED).
    // Pending-inactive ownership is bound only via authState (constructor) so a
    // transient getUid null during token refresh cannot clear suppression.
    this.authStateService.customTokenReady$.pipe(take(1)).subscribe(() => {
    this.commonService.getUid("StoreRepositoryService").subscribe({
      next: (userId) => {
        if (!userId) {
          return "No userId found";
        }
        const path = `/users/${userId}/jackpot_trigger`;
        this.storeFirestoreHelper.getAngularFireStore(path).subscribe({
          next: (res: Data) => {
            this.userJackpotTrigger$.next(this.applyPendingInactive(res));
          },
          error: (err) => {
            return err;
          },
        });
      },
      error: (err) => {
        return err;
      },
    });
    }); // end customTokenReady$ gate
  }

  /**
   * Optimistically mark a user jackpot trigger inactive so remapped
   * activeJackpots cannot rehydrate it as active before Firestore catches up.
   */
  markUserJackpotTriggerInactive(triggerId: string | number): void {
    if (triggerId === null || triggerId === undefined || triggerId === "") {
      return;
    }

    const targetId = String(triggerId);
    this.pendingInactiveTriggerIds.add(targetId);

    const current = this.userJackpotTrigger$.value;
    if (!Array.isArray(current)) {
      return;
    }

    this.userJackpotTrigger$.next(this.applyPendingInactive(current));
  }

  /**
   * Scope pending-inactive suppression to the current authenticated user.
   * Canonical source is AngularFireAuth.authState only (see constructor).
   */
  private bindPendingInactiveToUser(userId: string | null): void {
    if (!userId) {
      this.clearPendingInactiveTriggers();
      return;
    }

    if (
      this.pendingInactiveOwnerUserId &&
      this.pendingInactiveOwnerUserId !== userId
    ) {
      this.pendingInactiveTriggerIds.clear();
    }

    this.pendingInactiveOwnerUserId = userId;
  }

  private clearPendingInactiveTriggers(): void {
    this.pendingInactiveTriggerIds.clear();
    this.pendingInactiveOwnerUserId = null;
  }

  /**
   * Keep pending-inactive IDs forced off until Firestore confirms inactive
   * (`status === false`) or auth clears the set (logout / user switch).
   * Do not drop pending IDs merely because a trigger is absent from the current
   * emission — empty/partial loads would otherwise undo suppression too early.
   */
  private applyPendingInactive(triggers: Data): Data {
    if (!Array.isArray(triggers) || this.pendingInactiveTriggerIds.size === 0) {
      return triggers;
    }

    return triggers.map((trigger) => {
      if (trigger == null || trigger.id === null || trigger.id === undefined) {
        return trigger;
      }

      const id = String(trigger.id);
      if (!this.pendingInactiveTriggerIds.has(id)) {
        return trigger;
      }

      if (trigger.status === false) {
        this.pendingInactiveTriggerIds.delete(id);
        return trigger;
      }

      return { ...trigger, status: false };
    });
  }
}
